import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import { ACCENT } from "../data/studioStory";

// Restrained, functional diagrams ported from the Skylark /t/arch page. Each is
// plain inline SVG: monochrome (currentColor, inherited from the frame's text
// color so it adapts to dark/light) with the single warm-orange brand accent.
// Coordinates are unchanged from the original — only the wrapper is MUI now.

// `minWidth` is the load-bearing prop. An SVG set to width:100% scales its TEXT
// down with the box, so a 680-unit viewBox full of 11.5-unit labels renders at
// ~5px on a 390px phone — present, and unreadable. Below `minWidth` the diagram
// stops shrinking and the frame scrolls sideways instead, which keeps every
// label above the text-size floor at any viewport. Set it to the narrowest
// width at which that particular diagram's smallest label still reads.
export function DiagramFrame({ label, viewBox, minWidth = 640, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // currentColor for the diagram strokes/text — a muted neutral that reads in
  // both modes (the original used neutral-600 / neutral-300).
  const ink = isDark ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.68)";

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        // tabIndex makes the scroll region reachable by keyboard — without it a
        // keyboard-only visitor can never see the right-hand side of a diagram
        // that overflows. The svg's aria-label carries the full description, so
        // screen-reader users are not relying on the scroll either way.
        tabIndex={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark
            ? alpha(theme.palette.common.black, 0.25)
            : alpha(theme.palette.common.black, 0.02),
          px: 2,
          py: 2.5,
          overflowX: "auto",
        }}
      >
        <Box
          component="svg"
          viewBox={viewBox}
          role="img"
          aria-label={label}
          sx={{
            display: "block",
            width: "100%",
            minWidth,
            height: "auto",
            color: ink,
            fontFamily: '"Roboto", system-ui, "Helvetica", "Arial", sans-serif',
          }}
        >
          {children}
        </Box>
      </Box>
      {/* Below md the container is narrower than every diagram's minWidth, so
          the frame always clips. A clipped diagram with no affordance reads as
          broken rather than scrollable — say so. */}
      <Typography
        aria-hidden="true"
        sx={{
          display: { xs: "block", md: "none" },
          mt: 0.75,
          fontSize: "0.78rem",
          color: "text.secondary",
        }}
      >
        Scroll the diagram sideways to see all of it →
      </Typography>
    </Box>
  );
}

// The org chart. Four orchestrators report to the founder — one for the product
// portfolio, three for client engagements. Each runs its own fleet, and every
// agent in every fleet can open the same three kinds of helper below it.
//
// Client engagements are deliberately unnamed: "Client engagement A/B/C". The
// structure is the point and it's mine to describe; who the clients are is
// theirs. Do not put a client name, product name, or repo name in this diagram.
export function DiagramOrgChart() {
  // Four columns laid out across the 680-unit viewBox with 6 units of margin and
  // 21 between. Widths and centres are computed from those constants rather than
  // hand-placed: the first hand-placed version put column four at cx 630 with a
  // width of 150, which ran 25 units past the right edge and sat on top of
  // column three. If you change a width, change it here and let the maths move
  // the centres.
  const W_STUDIO = 178;
  const W_CLIENT = 142;
  const GAP = 21;
  const x0 = 6;
  const lefts = [x0, x0 + W_STUDIO + GAP, x0 + W_STUDIO + GAP + W_CLIENT + GAP, x0 + W_STUDIO + GAP + 2 * (W_CLIENT + GAP)];
  const orchs = [
    { w: W_STUDIO, x: lefts[0], label: "Studio", sub: "portfolio orchestrator", fleet: "one agent per product", accent: true },
    { w: W_CLIENT, x: lefts[1], label: "Client A", sub: "engagement orchestrator", fleet: "2–6 build lanes" },
    { w: W_CLIENT, x: lefts[2], label: "Client B", sub: "engagement orchestrator", fleet: "2–6 build lanes" },
    { w: W_CLIENT, x: lefts[3], label: "Client C", sub: "engagement orchestrator", fleet: "2–6 build lanes" },
  ].map((o) => ({ ...o, cx: o.x + o.w / 2 }));

  return (
    <DiagramFrame
      viewBox="0 0 680 470"
      minWidth={710}
      label="An org chart. At the top, the founder, who makes only the calls a human must. Below, four orchestrators — a studio orchestrator for the product portfolio, and one for each of three client engagements. The studio orchestrator runs one agent per product; each client orchestrator runs two to six build lanes. Any agent can open three kinds of helper beneath it: sub-agents that do a scoped piece of work, adversarial reviewers on a second AI model whose only job is to try to break the work, and deep-research agents that go and read the field. Underneath everything runs a shared message channel every agent can see."
    >
      {/* founder */}
      <rect x="258" y="4" width="164" height="42" rx="8" fill="none" stroke="currentColor" strokeOpacity="0.55" />
      <text x="340" y="30" textAnchor="middle" fontSize="17" fill="currentColor">The founder</text>
      <text x="340" y="60" textAnchor="middle" fontSize="12.5" fill="currentColor" fillOpacity="0.75">only the calls a human must — taste, money, anything irreversible</text>

      {/* founder -> orchestrator bus */}
      <line x1="340" y1="46" x2="340" y2="70" stroke="currentColor" strokeOpacity="0.5" />
      <line x1={orchs[0].cx} y1="70" x2={orchs[orchs.length - 1].cx} y2="70" stroke="currentColor" strokeOpacity="0.35" />
      {orchs.map((o) => (
        <line key={`d-${o.cx}`} x1={o.cx} y1="70" x2={o.cx} y2="84" stroke="currentColor" strokeOpacity="0.35" />
      ))}

      {/* orchestrator row */}
      {orchs.map((o) => (
        <g key={o.label}>
          <rect
            x={o.x}
            y="84"
            width={o.w}
            height="52"
            rx="8"
            fill="none"
            stroke={o.accent ? ACCENT : "currentColor"}
            strokeOpacity={o.accent ? 1 : 0.55}
            strokeWidth={o.accent ? 1.75 : 1}
          />
          <text x={o.cx} y="105" textAnchor="middle" fontSize={o.accent ? 15 : 14} fill="currentColor" fontWeight="600">
            {o.label}
          </text>
          <text x={o.cx} y="122" textAnchor="middle" fontSize="11.5" fill="currentColor" fillOpacity="0.75">
            {o.sub}
          </text>
          {/* down to its fleet */}
          <line x1={o.cx} y1="136" x2={o.cx} y2="160" stroke="currentColor" strokeOpacity="0.4" />
          <rect x={o.x} y="160" width={o.w} height="34" rx="6" fill="currentColor" fillOpacity="0.05" stroke="currentColor" strokeOpacity="0.35" strokeDasharray="3 3" />
          <text x={o.cx} y="181" textAnchor="middle" fontSize="12" fill="currentColor" fillOpacity="0.9">
            {o.fleet}
          </text>
        </g>
      ))}
      <text x="340" y="212" textAnchor="middle" fontSize="12.5" fill="currentColor" fillOpacity="0.75">
        Each agent owns one thing and knows it deeply. It runs the day; it doesn&rsquo;t wait to be told.
      </text>

      {/* the helper tier — what any agent can open beneath itself */}
      <line x1="340" y1="220" x2="340" y2="240" stroke="currentColor" strokeOpacity="0.4" />
      <path d="M336 234 L344 234 L340 242 Z" fill="currentColor" fillOpacity="0.7" />
      <text x="340" y="258" textAnchor="middle" fontSize="13" fill="currentColor" fillOpacity="0.9">
        Any agent can open three kinds of helper beneath itself:
      </text>

      {[
        {
          x: 14,
          title: "Sub-agents",
          count: "2–6 at a time",
          body: ["One scoped piece of work each,", "on its own copy of the code so", "they can't collide."],
        },
        {
          x: 236,
          title: "Adversarial reviewers",
          count: "1–3 per risky change",
          body: ["A second, rival AI model whose", "only job is to break the work.", "Findings are claims until proven."],
          accent: true,
        },
        {
          x: 458,
          title: "Deep-research agents",
          count: "as the question demands",
          body: ["Sent to read the field —", "the literature, the competitors,", "the prior art — and report back."],
        },
      ].map((h) => (
        <g key={h.title}>
          <rect
            x={h.x}
            y="270"
            width="208"
            height="106"
            rx="8"
            fill={h.accent ? ACCENT : "currentColor"}
            fillOpacity={h.accent ? 0.07 : 0.03}
            stroke={h.accent ? ACCENT : "currentColor"}
            strokeOpacity={h.accent ? 0.6 : 0.45}
          />
          <text x={h.x + 104} y="291" textAnchor="middle" fontSize="13.5" fill="currentColor" fontWeight="600">
            {h.title}
          </text>
          <text x={h.x + 104} y="307" textAnchor="middle" fontSize="11.5" fill={h.accent ? ACCENT : "currentColor"} fillOpacity={h.accent ? 1 : 0.7}>
            {h.count}
          </text>
          {h.body.map((line, i) => (
            <text key={i} x={h.x + 104} y={326 + i * 15} textAnchor="middle" fontSize="11.5" fill="currentColor" fillOpacity="0.8">
              {line}
            </text>
          ))}
        </g>
      ))}

      {/* the seat layer */}
      <rect x="14" y="392" width="652" height="40" rx="8" fill="none" stroke="currentColor" strokeOpacity="0.4" strokeDasharray="4 3" />
      <text x="340" y="410" textAnchor="middle" fontSize="12.5" fill="currentColor" fontWeight="600">
        Every seat above draws on the same written procedures
      </text>
      <text x="340" y="425" textAnchor="middle" fontSize="11.5" fill="currentColor" fillOpacity="0.75">
        the studio&rsquo;s standing playbooks — a day driver, a quality pass, an audit — written once, run by whoever holds the seat
      </text>

      {/* shared channel */}
      <rect x="14" y="444" width="652" height="20" rx="10" fill={ACCENT} fillOpacity="0.12" stroke={ACCENT} strokeOpacity="0.5" />
      <text x="340" y="458" textAnchor="middle" fontSize="12.5" fill="currentColor">
        one shared message channel — every agent posts to it, every agent can read it
      </text>
    </DiagramFrame>
  );
}

// The five-step daily loop, repeating.
export function DiagramDaily() {
  const steps = ["Reflect", "Reconcile", "Prioritize", "Do", "Tidy"];
  const w = 122;
  const xs = [2, 140, 278, 416, 554];
  return (
    <DiagramFrame
      viewBox="0 0 678 154"
      label="Every day each product runs the same five-step loop: reflect, reconcile its facts, prioritize the one thing that matters, do it, then tidy up — and the loop repeats the next day."
    >
      {steps.map((s, i) => (
        <g key={s}>
          <rect x={xs[i]} y="18" width={w} height="46" rx="23" fill="none" stroke="currentColor" strokeOpacity="0.5" />
          <text x={xs[i] + w / 2} y="46" textAnchor="middle" fontSize="16" fill="currentColor">{s}</text>
          {i < steps.length - 1 && (
            <g>
              <line x1={xs[i] + w + 3} y1="41" x2={xs[i + 1] - 5} y2="41" stroke="currentColor" strokeOpacity="0.5" />
              <path d={`M${xs[i + 1] - 11} 36 L${xs[i + 1] - 11} 46 L${xs[i + 1] - 3} 41 Z`} fill="currentColor" />
            </g>
          )}
        </g>
      ))}
      {/* loop back */}
      <path d="M615 66 C 615 114 63 114 63 68" fill="none" stroke={ACCENT} strokeOpacity="0.8" />
      <path d="M58 76 L68 76 L63 66 Z" fill={ACCENT} />
      <text x="339" y="108" textAnchor="middle" fontSize="14" fill={ACCENT}>repeats every day</text>
    </DiagramFrame>
  );
}

// Work flows through one narrow point — the constraint to widen first.
export function DiagramConstraint() {
  return (
    <DiagramFrame
      viewBox="0 0 680 168"
      label="All the studio's work flows through one narrow point — the single constraint. Widening that one point speeds everything up; effort spent anywhere else is wasted."
    >
      <text x="340" y="20" textAnchor="middle" fontSize="15" fill="currentColor" fillOpacity="0.85">Everything flows through one narrow point.</text>
      {/* left funnel */}
      <path d="M30 46 L320 76 L320 106 L30 136 Z" fill="currentColor" fillOpacity="0.06" stroke="currentColor" strokeOpacity="0.5" />
      {/* right funnel */}
      <path d="M650 46 L360 76 L360 106 L650 136 Z" fill="currentColor" fillOpacity="0.06" stroke="currentColor" strokeOpacity="0.5" />
      {/* throat / constraint */}
      <rect x="320" y="76" width="40" height="30" fill={ACCENT} fillOpacity="0.85" />
      {/* inflow arrows */}
      {[72, 134, 196].map((x) => (
        <g key={x}>
          <line x1={x} y1="91" x2={x + 38} y2="91" stroke="currentColor" strokeOpacity="0.45" />
          <path d={`M${x + 32} 86 L${x + 32} 96 L${x + 42} 91 Z`} fill="currentColor" fillOpacity="0.6" />
        </g>
      ))}
      {/* outflow arrow */}
      <line x1="470" y1="91" x2="612" y2="91" stroke="currentColor" strokeOpacity="0.45" />
      <path d="M606 86 L606 96 L616 91 Z" fill="currentColor" fillOpacity="0.6" />
      <text x="120" y="162" textAnchor="middle" fontSize="13" fill="currentColor" fillOpacity="0.75">work</text>
      <text x="340" y="128" textAnchor="middle" fontSize="14" fill={ACCENT} fontWeight="600">the one constraint</text>
      <text x="340" y="162" textAnchor="middle" fontSize="13" fill="currentColor" fillOpacity="0.75">widen this first — everything else waits</text>
      <text x="560" y="162" textAnchor="middle" fontSize="13" fill="currentColor" fillOpacity="0.75">throughput</text>
    </DiagramFrame>
  );
}

// The seat map: what the studio's written procedures actually cover. Counts are
// real and come from the procedure library — keep them in step with
// SEATS in studioStory.js, which is the single source both this and the prose
// read from. Describes capability, never implementation: no command names.
export function DiagramSeats() {
  const cols = [
    { x: 14, title: "Running the day", n: 12, body: ["Start a day, hand out work,", "audit what came back, close out,", "carry memory to tomorrow."] },
    { x: 236, title: "Product quality", n: 10, body: ["Walk a flow as a cold user,", "test copy on synthetic users,", "audit a screen against the canon."], accent: true },
    { x: 458, title: "Engineering health", n: 6, body: ["Triage errors and alerts,", "fix a broken build, audit a repo", "against the engineering bar."] },
    { x: 125, y: 1, title: "Knowledge", n: 6, body: ["Commission deep research, read the", "literature, turn findings into a", "standard the whole fleet is held to."] },
    { x: 347, y: 1, title: "The business", n: 3, body: ["Weekly finance pass, distribution,", "and pulling what shipped into", "something worth writing about."] },
  ];

  return (
    <DiagramFrame
      viewBox="0 0 680 300"
      minWidth={710}
      label="The studio's written procedures, grouped into five seats: twelve for running the day, ten for product quality, six for engineering health, six for knowledge and research, and three for the business. Thirty-seven in all, each one a procedure written once and run by whoever holds that seat."
    >
      <text x="340" y="18" textAnchor="middle" fontSize="13" fill="currentColor" fillOpacity="0.85">
        37 written procedures, grouped by the seat that runs them
      </text>

      {cols.map((c) => {
        const top = 32 + (c.y || 0) * 128;
        return (
          <g key={c.title}>
            <rect
              x={c.x}
              y={top}
              width="208"
              height="112"
              rx="8"
              fill={c.accent ? ACCENT : "currentColor"}
              fillOpacity={c.accent ? 0.07 : 0.03}
              stroke={c.accent ? ACCENT : "currentColor"}
              strokeOpacity={c.accent ? 0.6 : 0.45}
            />
            <text x={c.x + 104} y={top + 26} textAnchor="middle" fontSize="21" fill={c.accent ? ACCENT : "currentColor"} fontWeight="700">
              {c.n}
            </text>
            <text x={c.x + 104} y={top + 45} textAnchor="middle" fontSize="13" fill="currentColor" fontWeight="600">
              {c.title}
            </text>
            {c.body.map((line, i) => (
              <text key={i} x={c.x + 104} y={top + 65 + i * 15} textAnchor="middle" fontSize="11.5" fill="currentColor" fillOpacity="0.8">
                {line}
              </text>
            ))}
          </g>
        );
      })}

      <text x="340" y="292" textAnchor="middle" fontSize="12" fill="currentColor" fillOpacity="0.75">
        A procedure that gets written down stops depending on whoever happened to run it last.
      </text>
    </DiagramFrame>
  );
}

export function CardDiagram({ which }) {
  if (which === "orgchart") return <DiagramOrgChart />;
  if (which === "daily") return <DiagramDaily />;
  if (which === "constraint") return <DiagramConstraint />;
  if (which === "seats") return <DiagramSeats />;
  return null;
}
